export const UnipockPayment = () => ({
  orderId: null,
  paymentMethod: null,
  platform: null,
  openId: null,

  status: 'idle',
  qrCodeUrl: null,
  qrCodeImg: null,
  paymentUrl: null,
  error: null,

  pollingInterval: null,
  pollingCount: 0,
  maxPollingCount: 600,
  pollingIntervalMs: 1000,

  showCancelModal: false,
  isCancelling: false,

  paymentWindow: null,
  windowCheckInterval: null,
  windowFocusHandler: null,
  showManualCheck: false,

  init() {
    this.orderId = this.$el.dataset.orderId || '';
    this.platform = this.detectPlatform();

    window.addEventListener('unipock-payment-start', (event) => {
      this.startPayment(event.detail.method);
    });

    if (!this.orderId) {
      console.warn('Unipock payment: orderId is not set. Payment will fail.');
    }
  },

  destroy() {
    this.stopPolling();
    this.stopWindowMonitoring();
    if (this.paymentWindow && !this.paymentWindow.closed) {
      this.paymentWindow.close();
    }
  },

  detectPlatform() {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('micromessenger')) {
      return 'wechat_browser';
    }

    if (/android|iphone|ipad|ipod|mobile/i.test(ua)) {
      return 'mobile';
    }

    return 'desktop';
  },

  async startPayment(method) {
    this.paymentMethod = method;
    
    // 微信瀏覽器內選擇支付寶：攔截並提示
    if (this.platform === 'wechat_browser' && method === 'alipay') {
      this.status = 'error';
      this.error = '請在系統瀏覽器中打開此頁面以使用支付寶支付';
      return;
    }

    this.status = 'loading';
    this.error = null;
    this.qrCodeUrl = null;
    this.qrCodeImg = null;
    this.paymentUrl = null;

    try {
      const response = await this.createPayment();

      if (response.success) {
        if (response.qr_code_url) {
          this.qrCodeUrl = response.qr_code_url;
          this.status = 'qr_code';
          this.startPolling();
        } else if (response.qr_code_img) {
          this.qrCodeImg = response.qr_code_img;
          this.status = 'qr_code';
          this.startPolling();
        } else if (response.payment_html) {
          this.handleHtmlPayment(response.payment_html);
        } else if (response.payment_url) {
          this.paymentUrl = response.payment_url;
          this.status = 'redirecting';
          this.redirectToPayment();
        } else {
          this.error = '付款回應格式錯誤';
          this.status = 'error';
        }
      } else {
        this.error = response.error || '付款建立失敗';
        this.status = 'error';
      }
    } catch (e) {
      console.error('[Unipock Payment] 付款請求異常:', e);
      this.error = '付款請求失敗，請重試';
      this.status = 'error';
    }
  },

  async createPayment() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const body = {
      order_id: this.orderId,
      payment_method: this.paymentMethod,
      platform: this.platform
    };

    if (this.platform === 'wechat_browser' && this.openId) {
      body.open_id = this.openId;
    }

    const response = await fetch('/payments/create_unipock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(body)
    });


    const data = await response.json();

    return data;
  },

  handleHtmlPayment(htmlContent) {
    console.log('[Payment] Opening payment window...');
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();

      this.status = 'redirecting';
      this.paymentWindow = newWindow;
      
      console.log('[Payment] Payment window opened, will start monitoring in 1 second');
      setTimeout(() => {
        this.startPolling();
        this.startWindowMonitoring();
      }, 1000);
    } else {
      this.error = '無法開啟付款視窗，請檢查瀏覽器設定';
      this.status = 'error';
    }
  },

  startWindowMonitoring() {
    console.log('[Window Monitor] Starting monitoring...');
    
    this.windowFocusHandler = () => {
      console.log('[Window Monitor] Main window gained focus, checking payment window...');
      this.checkWindowClosed();
    };
    
    window.addEventListener('focus', this.windowFocusHandler);
    
    setTimeout(() => {
      this.showManualCheck = true;
      console.log('[Window Monitor] Showing manual check button after 3 seconds');
    }, 3000);
    
    let checkCount = 0;
    this.windowCheckInterval = setInterval(() => {
      checkCount++;
      this.checkWindowClosed(checkCount);
    }, 500);
  },
  
  checkWindowClosed(checkCount = 0) {
    if (!this.paymentWindow) {
      console.log('[Window Monitor] No payment window reference');
      this.stopWindowMonitoring();
      return;
    }
    
    try {
      const isClosed = this.paymentWindow.closed;
      
      if (checkCount > 0 && checkCount % 10 === 0) {
        console.log(`[Window Monitor] Check #${checkCount}: window.closed = ${isClosed}`);
      }
      
      if (isClosed) {
        console.log('[Window Monitor] Payment window closed detected!');
        this.handlePaymentWindowClosed();
      }
    } catch (e) {
      if (checkCount > 0 && checkCount % 10 === 0) {
        console.log(`[Window Monitor] Check #${checkCount}: Cross-origin blocked`);
      }
    }
  },
  
  manualCheckWindowClosed() {
    console.log('[Window Monitor] Manual check triggered by user');
    this.showManualCheck = false;
    this.handlePaymentWindowClosed();
  },

  stopWindowMonitoring() {
    if (this.windowCheckInterval) {
      console.log('[Window Monitor] Stopping monitoring');
      clearInterval(this.windowCheckInterval);
      this.windowCheckInterval = null;
    }
    
    if (this.windowFocusHandler) {
      window.removeEventListener('focus', this.windowFocusHandler);
      this.windowFocusHandler = null;
    }
  },

  async handlePaymentWindowClosed() {
    console.log('[Window Monitor] handlePaymentWindowClosed called, current status:', this.status);
    this.stopWindowMonitoring();

    if (this.status === 'success' || this.status === 'cancelled') {
      console.log('[Window Monitor] Already in final state, skipping');
      return;
    }

    console.log('[Window Monitor] Checking payment status one last time...');
    await this.checkPaymentStatusOnce();

    if (this.status !== 'success') {
      console.log('[Window Monitor] Payment not completed, showing window_closed UI');
      this.stopPolling();
      this.status = 'window_closed';
      this.error = '支付視窗已關閉。如果您已完成支付,請稍候系統會自動確認。';
    } else {
      console.log('[Window Monitor] Payment was successful!');
    }
  },

  async checkPaymentStatusOnce() {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/orders/${this.orderId}.json`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.state === 'paid' || data.state === 'shipped' || data.state === 'completed') {
          this.stopPolling();
          this.status = 'success';

          setTimeout(() => {
            window.location.href = `/orders/${this.orderId}`;
          }, 1500);
        }
      }
    } catch (e) {
      console.error('[Unipock Payment] 檢查付款狀態失敗:', e);
    }
  },

  redirectToPayment() {
    if (this.paymentUrl) {
      window.location.href = this.paymentUrl;
    }
  },

  startPolling() {
    this.pollingCount = 0;
    this.pollingInterval = setInterval(() => {
      this.checkPaymentStatus();
    }, this.pollingIntervalMs);
  },

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  },

  async checkPaymentStatus() {
    this.pollingCount++;

    if (this.pollingCount >= this.maxPollingCount) {
      this.stopPolling();
      this.status = 'timeout';
      this.error = '付款逾時，訂單已取消';
      await this.cancelOrderOnServer('timeout');
      return;
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/orders/${this.orderId}.json`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.state === 'paid' || data.state === 'shipped' || data.state === 'completed') {
          this.stopPolling();
          this.status = 'success';

          setTimeout(() => {
            window.location.href = `/orders/${this.orderId}`;
          }, 1500);
        }
      }
    } catch (e) {
      // Silent fail - continue polling
    }
  },

  openCancelModal() {
    this.showCancelModal = true;
  },

  closeCancelModal() {
    this.showCancelModal = false;
  },

  async confirmCancelPayment() {
    this.isCancelling = true;
    this.stopPolling();

    const result = await this.cancelOrderOnServer('cancelled');

    if (result.success) {
      this.showCancelModal = false;
      this.status = 'cancelled';
      this.error = '訂單已取消';

      setTimeout(() => {
        window.location.href = result.redirect_url || '/orders';
      }, 1500);
    } else {
      this.isCancelling = false;
      this.error = result.error || '取消訂單失敗';
      this.status = 'error';
      this.showCancelModal = false;
    }
  },

  async cancelOrderOnServer(reason) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/payments/cancel_unipock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          order_id: this.orderId,
          reason: reason
        })
      });

      const data = await response.json();

      if (data.success) {
        setTimeout(() => {
          window.location.href = data.redirect_url || '/orders';
        }, 2000);
      }

      return data;
    } catch (e) {
      console.error('[Unipock Payment] 取消訂單失敗:', e);
      return { success: false, error: '取消訂單時發生錯誤' };
    }
  },

  retryPayment() {
    if (this.paymentMethod) {
      this.startPayment(this.paymentMethod);
    }
  },

  get isLoading() {
    return this.status === 'loading';
  },

  get showQrCode() {
    return this.status === 'qr_code' && (this.qrCodeUrl || this.qrCodeImg);
  },

  get qrCodeSrc() {
    if (this.qrCodeImg) {
      return this.qrCodeImg;
    }
    if (this.qrCodeUrl) {
      return 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(this.qrCodeUrl);
    }
    return '';
  },

  get showRedirecting() {
    return this.status === 'redirecting';
  },

  get showError() {
    return this.status === 'error';
  },

  get showTimeout() {
    return this.status === 'timeout';
  },

  get showCancelled() {
    return this.status === 'cancelled';
  },

  get showSuccess() {
    return this.status === 'success';
  },

  get showWindowClosed() {
    return this.status === 'window_closed';
  },

  get paymentMethodLabel() {
    const labels = {
      'wechat_pay': '微信支付',
      'alipay': '支付寶'
    };
    return labels[this.paymentMethod] || this.paymentMethod;
  },

  get statusMessage() {
    const messages = {
      'idle': '',
      'loading': '正在建立付款...',
      'qr_code': '請使用手機掃描 QR Code 完成付款',
      'redirecting': '正在跳轉至付款頁面...',
      'success': '付款成功！正在跳轉...',
      'error': this.error,
      'timeout': '付款逾時，訂單已取消，正在跳轉...',
      'cancelled': '訂單已取消，正在跳轉...',
      'window_closed': this.error
    };
    return messages[this.status] || '';
  },

  get remainingTime() {
    const remainingMs = (this.maxPollingCount - this.pollingCount) * this.pollingIntervalMs;
    const remainingSeconds = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
});
